import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import { POSTS } from "../data/blog";
import { Seo, breadcrumbLd, SITE_URL } from "../components/Seo";
import { ShareBar } from "../components/ShareBar";
import NotFound from "./NotFound";
import { FlowImage } from "../components/FlowImage";

const fmt = (d) => new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

const PostCard = ({ p, big }) => (
  <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className={`img-zoom group flex flex-col ${big ? "md:col-span-2 md:grid md:grid-cols-2 md:gap-8" : ""}`}>
    <FlowImage src={p.image} alt={p.title} fade="soft" speed={0.03} className="aspect-[4/3] rounded-2xl" />
    <div className={big ? "mt-5 flex flex-col justify-center md:mt-0" : "mt-5"}>
      <p className="eyebrow">{p.tag} · {p.read} min read</p>
      <h2 className={`mt-3 ${big ? "t-h2" : "t-h3"} transition-colors group-hover:text-sheesham`}>{p.title}</h2>
      <p className="t-body mt-3 text-muted-foreground">{p.excerpt}</p>
      <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-sheesham">Read <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
    </div>
  </Link>
);

export function BlogList() {
  return (
    <div data-testid="blog-page" className="container-hw py-10 sm:py-16">
      <Seo title="DIY Modular Furniture Blog – Guides, Styling & Build Tips | H&W Furniture" description="Guides on DIY modular furniture, flat-pack assembly tips, and bookshelf styling ideas for Indian homes from H&W Furniture, Hyderabad." path="/blog" jsonLd={[breadcrumbLd([["Home", "/"], ["Blog", "/blog"]])]} />
      <p className="eyebrow">The H&amp;W Journal</p>
      <h1 className="t-h1 mt-3">Build, style and live with modular furniture.</h1>
      <p className="t-lead mt-4 max-w-xl">Practical guides from our Hyderabad workshop floor: how to build flat-pack furniture, style a small flat, and choose what lasts.</p>
      <div className="mt-12 grid gap-x-8 gap-y-14 md:grid-cols-2">
        {POSTS.map((p, i) => <PostCard key={p.slug} p={p} big={i === 0} />)}
      </div>
    </div>
  );
}

const Block = ({ b: [type, content] }) => {
  if (type === "h2") return <h2 className="t-h3 mt-10">{content}</h2>;
  if (type === "ul" || type === "ol") {
    const L = type;
    return <L className={`mt-4 space-y-2 pl-5 ${type === "ul" ? "list-disc" : "list-decimal"} marker:text-brass`}>{content.map((x) => <li key={x}>{x}</li>)}</L>;
  }
  return <p className="t-body mt-4">{content}</p>;
};

export function BlogPost() {
  const { slug } = useParams();
  const p = POSTS.find((x) => x.slug === slug);
  if (!p) return <NotFound />;
  const more = POSTS.filter((x) => x.slug !== slug).slice(0, 2);
  const ld = {
    "@context": "https://schema.org", "@type": "BlogPosting", headline: p.title, description: p.excerpt, image: [p.image],
    datePublished: p.date, dateModified: p.date, author: { "@type": "Organization", name: "H&W Furniture" },
    publisher: { "@type": "Organization", name: "H&W furnispace Private Limited", logo: { "@type": "ImageObject", url: `${SITE_URL}/logo512.png` } },
    mainEntityOfPage: `${SITE_URL}/blog/${p.slug}`,
  };
  return (
    <article data-testid="blog-post" className="container-hw py-12 sm:py-16">
      <Seo title={`${p.title} | H&W Furniture`} description={p.excerpt} path={`/blog/${p.slug}`} type="article" jsonLd={[ld, breadcrumbLd([["Home", "/"], ["Blog", "/blog"], [p.title, `/blog/${p.slug}`]])]} />
      <div className="mx-auto max-w-3xl">
        <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> All articles</Link>
        <p className="eyebrow mt-8">{p.tag}</p>
        <h1 className="t-h1 mt-3">{p.title}</h1>
        <p className="t-small mt-4 flex items-center gap-2"><time dateTime={p.date}>{fmt(p.date)}</time> · <Clock className="h-3.5 w-3.5" /> {p.read} min read</p>
      </div>
      <FlowImage src={p.image} alt={p.title} fade="bottom" testid="blog-hero-image" className="mx-auto mt-10 aspect-[16/9] w-full max-w-5xl rounded-[2rem]" />
      <div className="mx-auto mt-10 max-w-2xl text-foreground/90">
        {p.body.map((b, i) => <Block key={i} b={b} />)}
        <div className="card mt-12 p-6 sm:p-8">
          <p className="t-h3">Ready to build your own?</p>
          <p className="t-small mt-2">The Pustak Rack: 7 shelves, Oak or Walnut, 24 Hrs free delivery in Hyderabad.</p>
          <Link to="/product/pustak-rack" data-testid="blog-cta-button" className="btn-sheesham mt-5">Shop the Pustak Rack</Link>
        </div>
        <div className="mt-8"><ShareBar path={`/blog/${p.slug}`} title={p.title} text={p.title} image={p.image} testid="blog-share" /></div>
      </div>
      <div className="mx-auto mt-20 grid max-w-5xl gap-10 border-t border-jute/50 pt-12 md:grid-cols-2">
        {more.map((x) => <PostCard key={x.slug} p={x} />)}
      </div>
    </article>
  );
}
